import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_PLATFORM_VALUES,
  ACCOUNT_STATUS_VALUES,
  type AccountPlatform,
  type AccountStatus,
} from '@content-engine/shared';
import {
  createAccount,
  deleteAccount,
  fetchAccounts,
  updateAccount,
} from '../lib/queries.ts';
import type { Account } from '../lib/types.ts';

const PLATFORM_ICON: Record<AccountPlatform, { className: string; emoji: string }> = {
  telegram: { className: 'tg', emoji: '✈' },
  tiktok: { className: 'tt', emoji: '🎵' },
};

function useRelativeTime() {
  const { t } = useTranslation();
  return (iso: string | null): string => {
    if (!iso) return t('accounts.card.lastNever');
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60_000);
    if (mins < 1) return t('accounts.card.lastNow');
    if (mins < 60) return t('accounts.card.lastMin', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('accounts.card.lastHour', { count: hours });
    return t('accounts.card.lastDay', { count: Math.floor(hours / 24) });
  };
}

export function AccountsPage(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => fetchAccounts() });
  const accounts = accountsQuery.data?.accounts ?? [];

  const groups = ACCOUNT_PLATFORM_VALUES.map((platform) => ({
    platform,
    accounts: accounts.filter((a) => a.platform === platform),
  }));

  const totalActive = accounts.filter((a) => a.status === 'active').length;
  const totalPaused = accounts.filter((a) => a.status === 'paused').length;
  const totalBanned = accounts.filter((a) => a.status === 'banned').length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('accounts.title')}</h1>
          <div className="sub">
            {t('accounts.subtitle', {
              count: accounts.length,
              platforms: ACCOUNT_PLATFORM_VALUES.length,
            })}
          </div>
        </div>
        <div className="filters">
          <button className="primary" onClick={() => setShowCreate(true)}>
            {t('accounts.create')}
          </button>
        </div>
      </div>

      <div className="metric-row">
        <div className="metric">
          <div className="label">📱 {t('accounts.metricTotal')}</div>
          <div className="value">{accounts.length}</div>
          <div className="delta">{t('accounts.metricTotalHint')}</div>
        </div>
        <div className="metric">
          <div className="label">⚡ {t('accounts.metricActive')}</div>
          <div className="value">{totalActive}</div>
          <div className="delta">
            {accounts.length > 0
              ? t('accounts.metricActiveHint', {
                  percent: Math.round((totalActive / accounts.length) * 100),
                })
              : t('accounts.metricActiveEmpty')}
          </div>
        </div>
        <div className="metric amber">
          <div className="label">⏸ {t('accounts.metricPaused')}</div>
          <div className="value">{totalPaused}</div>
          <div className="delta">{t('accounts.metricPausedHint')}</div>
        </div>
        <div className="metric hot">
          <div className="label">⛔ {t('accounts.metricBanned')}</div>
          <div className="value">{totalBanned}</div>
          <div className="delta down">{t('accounts.metricBannedHint')}</div>
        </div>
      </div>

      {groups.map((group) => (
        <div className="section" key={group.platform} style={{ marginBottom: 16 }}>
          <div className="section-head">
            <h2>
              {t(`accounts.platforms.${group.platform}`)}{' '}
              <span className="count">{group.accounts.length}</span>
            </h2>
          </div>
          <div className="section-body" style={{ padding: 12 }}>
            {group.accounts.length === 0 && (
              <div style={{ color: 'var(--text-3)', padding: 8, fontSize: 13 }}>
                {t('accounts.noneOnPlatform')}
              </div>
            )}
            {group.accounts.map((acc) => (
              <AccountCard
                key={acc.id}
                account={acc}
                onEdit={() => setEditing(acc)}
                onDelete={async () => {
                  if (confirm(t('accounts.deleteConfirm', { handle: acc.handle }))) {
                    await deleteAccount(acc.id);
                    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
                  }
                }}
              />
            ))}
          </div>
        </div>
      ))}

      {showCreate && (
        <AccountFormDialog
          mode="create"
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
          }}
        />
      )}

      {editing && (
        <AccountFormDialog
          mode="edit"
          account={editing}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
          }}
        />
      )}
    </>
  );
}

interface AccountCardProps {
  account: Account;
  onEdit: () => void;
  onDelete: () => void;
}

function AccountCard({ account, onEdit, onDelete }: AccountCardProps): React.ReactElement {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const icon = PLATFORM_ICON[account.platform];
  const pillKind =
    account.status === 'active' ? 'live' : account.status === 'paused' ? 'idle' : 'banned';

  return (
    <div className="account-card">
      <div className={`ico ${icon.className}`}>{icon.emoji}</div>
      <div className="body">
        <div className="name">{account.handle}</div>
        <div className="meta">
          {account.displayName ? `${account.displayName} · ` : ''}
          {account.proxyUrl ? `proxy: ${new URL(account.proxyUrl).hostname} · ` : ''}
          last: {relativeTime(account.lastPublishedAt)}
        </div>
        <div className="stats">
          <span>
            <strong>{t('accounts.card.limitDay', { count: account.maxPostsPerDay })}</strong>
          </span>
          <span>
            <strong>{t('accounts.card.intervalMin', { count: account.minMinutesBetweenPosts })}</strong>
          </span>
          {account.notes && <span style={{ color: 'var(--text-3)' }}>{account.notes}</span>}
        </div>
      </div>
      <div className="right-col">
        <span className={`pill ${pillKind}`}>{account.status}</span>
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            onClick={onEdit}
            style={{
              background: 'transparent',
              border: '1px solid var(--line-strong)',
              color: 'var(--text-2)',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            ✎ {t('common.edit')}
          </button>
          <button
            onClick={onDelete}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,61,110,0.3)',
              color: 'var(--hot)',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

interface AccountFormDialogProps {
  mode: 'create' | 'edit';
  account?: Account;
  onClose: () => void;
  onSuccess: () => void;
}

function AccountFormDialog(props: AccountFormDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState<AccountPlatform>(props.account?.platform ?? 'telegram');
  const [handle, setHandle] = useState(props.account?.handle ?? '');
  const [displayName, setDisplayName] = useState(props.account?.displayName ?? '');
  const [status, setStatus] = useState<AccountStatus>(props.account?.status ?? 'active');
  const [proxyUrl, setProxyUrl] = useState(props.account?.proxyUrl ?? '');
  const [maxPostsPerDay, setMaxPostsPerDay] = useState(props.account?.maxPostsPerDay ?? 4);
  const [minMinutesBetweenPosts, setMinMinutes] = useState(
    props.account?.minMinutesBetweenPosts ?? 30,
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        platform,
        handle,
        displayName: displayName || null,
        status,
        proxyUrl: proxyUrl || null,
        maxPostsPerDay,
        minMinutesBetweenPosts,
      };
      if (props.mode === 'edit' && props.account) {
        await updateAccount(props.account.id, payload);
      } else {
        await createAccount(payload);
      }
    },
    onSuccess: props.onSuccess,
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 520,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>
          {props.mode === 'create'
            ? t('accounts.form.createTitle')
            : t('accounts.form.editTitle', { handle })}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row inline">
            <div>
              <label>{t('accounts.form.platform')}</label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as AccountPlatform)}
                disabled={props.mode === 'edit'}
              >
                {ACCOUNT_PLATFORM_VALUES.map((p) => (
                  <option key={p} value={p}>
                    {t(`accounts.platforms.${p}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>{t('accounts.form.status')}</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as AccountStatus)}>
                {ACCOUNT_STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <label>{t('accounts.form.handle')}</label>
            <input
              type="text"
              required
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder={t('accounts.form.handlePlaceholder')}
              autoFocus
            />
          </div>
          <div className="form-row">
            <label>{t('accounts.form.displayName')}</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('accounts.form.displayNamePlaceholder')}
            />
          </div>
          <div className="form-row">
            <label>{t('accounts.form.proxyUrl')}</label>
            <input
              type="url"
              value={proxyUrl}
              onChange={(e) => setProxyUrl(e.target.value)}
              placeholder={t('accounts.form.proxyPlaceholder')}
            />
          </div>
          <div className="form-row inline">
            <div>
              <label>{t('accounts.form.postsPerDay')}</label>
              <input
                type="number"
                min={1}
                max={50}
                value={maxPostsPerDay}
                onChange={(e) => setMaxPostsPerDay(Number(e.target.value))}
              />
            </div>
            <div>
              <label>{t('accounts.form.intervalMin')}</label>
              <input
                type="number"
                min={1}
                max={1440}
                value={minMinutesBetweenPosts}
                onChange={(e) => setMinMinutes(Number(e.target.value))}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(255,61,110,0.1)',
                border: '1px solid rgba(255,61,110,0.3)',
                borderRadius: 6,
                color: 'var(--hot)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={props.onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="primary" disabled={mutation.isPending}>
              {mutation.isPending
                ? t('accounts.form.submitting')
                : props.mode === 'create'
                  ? t('accounts.form.submitCreate')
                  : t('accounts.form.submitEdit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
